/**
 * Central registry of recently-shipped features. Mirrors the `since` dates
 * used in `<NewBadge>` calls scattered across the app — but in one place
 * so the "What's New" drawer can render a chronological feed of changes.
 *
 * Conventions:
 *   - `since` = ISO date the feature shipped (use the same value as the
 *     paired NewBadge so the two stay in sync).
 *   - `path` = where to navigate when the user clicks the entry (omit for
 *     items without a dedicated page).
 *   - `featureId` = stable id (matches the NewBadge featureId where used)
 *     so per-user dismissal is shared between badge + drawer.
 *   - `daysVisible` = override the default 7-day visibility (use sparingly).
 *
 * To add a new entry: prepend to the top of FEATURES (most-recent-first).
 * The drawer auto-hides items past their visibility window — no manual
 * cleanup needed.
 */
export const FEATURES = [
  {
    featureId: 'whats-new-drawer-launch',
    since: '2026-04-27',
    title: "What's New drawer",
    description: 'Centralised feed of recent shipped features — opens from the sparkle icon in the top bar.',
    path: null,
    category: 'UX',
    targetTestId: 'whats-new-button',
    tourHint: "This sparkle icon is your one-stop feed of every recent change. Click it any time you've been away.",
  },
  {
    featureId: 'route-health-render-errors',
    since: '2026-04-27',
    title: 'Client-side render error beacon',
    description: 'Catches blank pages caused by JS crashing on mount. Visible on the Route Health page.',
    path: '/route-health',
    category: 'Reliability',
    targetTestId: 'render-error-top-paths',
    tourHint: "When a page crashes for a real user, it shows up here within seconds — even if the HTTP probe says everything's green.",
  },
  {
    featureId: 'login-build-stamp-shipped',
    since: '2026-04-27',
    title: 'Build stamp + deployment freshness banner',
    description: 'Login page now shows the running commit + build time. A banner pops up automatically when a newer build is deployed.',
    path: '/login',
    category: 'Reliability',
    targetTestId: 'login-build-stamp',
    tourHint: 'This stamp tells you exactly which build the user is running. Read it to support to confirm a deploy went out.',
  },
  {
    featureId: 'page-pos-mapping',
    since: '2026-04-26',
    title: 'POS Machine → Branch mapping',
    description: 'Map every POS terminal to its physical branch so bank-statement deposits attribute correctly.',
    path: '/pos-mapping',
    category: 'Reconciliation',
    targetTestId: null,
    tourHint: 'Map your POS terminal serial numbers to their branches once — every future deposit will route correctly.',
  },
  {
    featureId: 'page-route-health',
    since: '2026-04-25',
    title: 'Route Health Beacon',
    description: 'HTTP-level probe of every critical SPA + API route. Catches 404s, 5xxs, and broken redirects within seconds.',
    path: '/route-health',
    category: 'Reliability',
    targetTestId: null,
    tourHint: 'Click "Run probe now" to verify every critical page + API in seconds.',
  },
  {
    featureId: 'bank-statements-top3-picker',
    since: '2026-04-24',
    title: 'Top-3 candidate matches on bank statements',
    description: 'Unmatched bank rows now show the top 3 most-likely entities with confidence scores. One click resolves the match.',
    path: '/bank-statements',
    category: 'Reconciliation',
    targetTestId: 'bankstatements-title',
    tourHint: 'Unmatched rows now suggest the top 3 likely matches with confidence — one click resolves them.',
  },
  {
    featureId: 'page-bank-match-audit-narrate',
    since: '2026-04-23',
    title: 'Replay-as-Story narrator',
    description: 'AI-generated plain-English narrative of every bank match — see what happened in seconds without reading raw audit rows.',
    path: '/bank-match-audit',
    category: 'AI',
    targetTestId: null,
    tourHint: 'Click any row\'s "Replay as story" button for a plain-English forensic narrative.',
  },
  {
    featureId: 'platforms-bank-accounts',
    since: '2026-04-22',
    title: 'Bank Accounts editor on Platforms',
    description: 'Map each delivery platform (HungerStation, Jahez, ToYou…) to its receiving bank account so deposits attribute correctly.',
    path: '/platforms',
    category: 'Reconciliation',
    targetTestId: 'platforms-page-title',
    tourHint: 'Open any platform to set its receiving bank — deposits will then auto-attribute to the right account.',
  },
];

export const DEFAULT_DAYS_VISIBLE = 7;

/** Filter to items still in their visibility window. */
export function getActiveFeatures(now = Date.now()) {
  return FEATURES.filter((f) => {
    const sinceTs = Date.parse(f.since);
    if (Number.isNaN(sinceTs)) return false;
    const days = f.daysVisible || DEFAULT_DAYS_VISIBLE;
    return now - sinceTs <= days * 24 * 60 * 60 * 1000;
  });
}

/** Filter further by removing items the user has dismissed. */
export function getVisibleFeatures(now = Date.now()) {
  let dismissed = {};
  try {
    const raw = localStorage.getItem('smarto.newbadge.dismissed');
    if (raw) dismissed = JSON.parse(raw);
  } catch (_) {}
  return getActiveFeatures(now).filter((f) => !dismissed[f.featureId]);
}
